import { useCallback, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useAuth from '../../hooks/useAuth';
import useRefresh from '../../hooks/useRefresh';
import useBackofficeRefresh from '../../hooks/useBackofficeRefresh';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { clearEmployeeAndBusinessInfo } from '../../redux/AuthSlice';
import { logoutAction } from '../../redux/Store';
import { installGlobalAxiosInterceptors } from '../../services/installGlobalAxiosInterceptors';

export default function AxiosInterceptorBootstrap() {
  const { auth, setAuth } = useAuth();
  const ownerAuth = useAppSelector(s => s.backofficeOwnerAuth);
  const currentLocation = useAppSelector(s => s.location.currentLocation as any);
  const currentLocationId = useAppSelector(s => s.location.currentLocationId);
  const isAllLocations = useAppSelector(s => s.location.isAllLocations);

  const refreshPosHook = useRefresh();
  const { refresh: refreshBackofficeHook, clearOwnerSession } =
    useBackofficeRefresh();
  const dispatch = useAppDispatch();

  const authRef = useRef(auth);
  const ownerAuthRef = useRef(ownerAuth);
  const currentLocationRef = useRef(currentLocation);
  const currentLocationIdRef = useRef(currentLocationId);
  const isAllLocationsRef = useRef(isAllLocations);
  const refreshPosRef = useRef(refreshPosHook);
  const refreshBackofficeRef = useRef(refreshBackofficeHook);
  const clearOwnerSessionRef = useRef(clearOwnerSession);

  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  useEffect(() => {
    ownerAuthRef.current = ownerAuth;
  }, [ownerAuth]);

  useEffect(() => {
    currentLocationRef.current = currentLocation;
  }, [currentLocation]);

  useEffect(() => {
    currentLocationIdRef.current = currentLocationId;
  }, [currentLocationId]);

  useEffect(() => {
    isAllLocationsRef.current = isAllLocations;
  }, [isAllLocations]);

  useEffect(() => {
    refreshPosRef.current = refreshPosHook;
  }, [refreshPosHook]);

  useEffect(() => {
    refreshBackofficeRef.current = refreshBackofficeHook;
  }, [refreshBackofficeHook]);

  useEffect(() => {
    clearOwnerSessionRef.current = clearOwnerSession;
  }, [clearOwnerSession]);

  const clearPosSession = useCallback(async () => {
    setAuth({ accessToken: '', refreshToken: '' });
    await AsyncStorage.multiRemove(['accessToken', 'refreshToken']);
    dispatch(clearEmployeeAndBusinessInfo());
    dispatch(logoutAction());
  }, [dispatch, setAuth]);

  useEffect(() => {
    return installGlobalAxiosInterceptors({
      getPosAccessToken: () => authRef.current?.accessToken,
      refreshPos: () => refreshPosRef.current(),
      onPosAuthFailure: clearPosSession,
      getPosHeaders: () => {
        const id = currentLocationIdRef.current;
        const headers: Record<string, string> = {};
        if (id) {
          headers['X-Location-ID'] = String(id);
        }
        return headers;
      },
      getBackofficeAccessToken: () => ownerAuthRef.current?.accessToken,
      refreshBackoffice: () => refreshBackofficeRef.current(),
      onBackofficeAuthFailure: () => clearOwnerSessionRef.current(),
      getBackofficeHeaders: () => {
        const headers: Record<string, string> = {};
        const locationId =
          currentLocationRef.current?._id || currentLocationIdRef.current || '';
        if (locationId) {
          headers['X-Location-ID'] = String(locationId);
        }
        if (isAllLocationsRef.current) {
          headers['x-all-locations'] = 'true';
        }
        return headers;
      },
    });
  }, [clearPosSession]);

  return null;
}
